import React, { useEffect, useState } from 'react'
import { Image, RefreshControl, Platform } from 'react-native'
import {
  Package, Store, Camera, ImagePlus, ChevronRight, Tag, ShoppingBag,
} from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import {
  Spinner, Text, Card, SectionHeader, StatCard, ActionCard, ListItem, EmptyState,
  Badge, BadgeText, YStack, XStack,
} from '@fliisi/pollen'
import { ScreenLayout } from '../components/ScreenLayout'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Home'>
}

interface DraftProduct {
  id: string; title: string | null; title_fi: string | null; title_en: string | null
  price: number | null; created_at: string; product_images: { image_url: string }[]
}

export default function HomeScreen({ navigation }: Props) {
  const { user } = useAuth()
  const { t, language } = useLanguage()
  const [drafts, setDrafts] = useState<DraftProduct[]>([])
  const [draftCount, setDraftCount] = useState(0)
  const [activeReservations, setActiveReservations] = useState(0)
  const [hasStores, setHasStores] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (!user?.id) { navigation.replace('Login'); return }
    fetchData()
  }, [user?.id])

  const fetchData = async () => {
    if (!user?.id) return
    try {
      const { data: draftData, count } = await supabase
        .from('products')
        .select('id, title, title_fi, title_en, price, created_at, product_images(image_url)', { count: 'exact' })
        .eq('seller_id', user.id)
        .is('reservation_id', null)
        .is('flea_market_id', null)
        .order('created_at', { ascending: false })
        .limit(6)
      setDrafts(draftData || [])
      setDraftCount(count || 0)

      const { count: resCount } = await supabase
        .from('space_reservations')
        .select('id', { count: 'exact', head: true })
        .eq('seller_id', user.id)
        .in('status', ['active', 'confirmed'])
      setActiveReservations(resCount || 0)

      const { count: storeCount } = await supabase
        .from('flea_market_staff')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
      setHasStores((storeCount || 0) > 0)
    } catch (e) { console.error('Error fetching seller data:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }

  const getTitle = (p: DraftProduct) => {
    if (language === 'fi' && p.title_fi) return p.title_fi
    if (language === 'en' && p.title_en) return p.title_en
    return p.title || t('unnamed_product')
  }

  return (
    <ScreenLayout
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData() }} />}
    >
        {/* Header */}
        <YStack px="$lg" pt={Platform.OS === 'web' ? 20 : 56} pb="$md">
          <Text fontFamily="$cursive" fontSize={28} color="$colorPrimary">
            {t('my_products')}
          </Text>
        </YStack>

        {/* Quick stats */}
        <XStack px="$lg" gap="$sm" mb="$lg">
          <StatCard f={1} label={t('drafts')} value={String(draftCount)} onPress={() => navigation.navigate('DraftProducts' as any)} />
          <StatCard f={1} label={t('reservations')} value={String(activeReservations)} onPress={() => navigation.navigate('JoinMarket')} />
        </XStack>

        {/* Add product */}
        <YStack px="$lg" mb="$lg">
          <Text preset="label" mb="$sm">{t('add_product')}</Text>
          <XStack gap="$sm">
            <ActionCard f={1} icon={<Camera size={24} color="#fff" />} title={t('camera')} onPress={() => navigation.navigate('Camera' as any)} />
            <ActionCard f={1} icon={<ImagePlus size={24} color="#fff" />} title={t('gallery')} onPress={() => navigation.navigate('Camera' as any, { pickFromGallery: true })} />
          </XStack>
        </YStack>

        {/* Recent drafts */}
        <YStack px="$lg" mb="$lg">
          <SectionHeader
            title={t('recent_drafts')}
            actionLabel={draftCount > 0 ? t('all') : undefined}
            onAction={() => navigation.navigate('DraftProducts' as any)}
            mb="$sm"
          />

          {loading ? (
            <YStack h={120} jc="center" ai="center"><Spinner size="small" /></YStack>
          ) : drafts.length === 0 ? (
            <EmptyState
              icon={<Tag size={36} color="#858B80" />}
              title={t('no_drafts')}
              description={t('take_photo_to_start')}
            />
          ) : (
            <XStack flexWrap="wrap" gap={10}>
              {drafts.map(draft => {
                const img = draft.product_images?.[0]?.image_url
                return (
                  <Card
                    key={draft.id} variant="elevated" w="48%" br="$lg" overflow="hidden" pressable
                    onPress={() => navigation.navigate('ProductDetail', { productId: draft.id })}
                  >
                    <YStack w="100%" aspectRatio={1} bg="#E0E2DB">
                      {img ? (
                        <Image source={{ uri: img }} style={{ width: '100%', height: '100%' }} />
                      ) : (
                        <YStack f={1} jc="center" ai="center" bg="#F5F5F0">
                          <Package size={20} color="#858B80" />
                        </YStack>
                      )}
                    </YStack>
                    <YStack p="$sm">
                      <Text preset="label" numberOfLines={1}>{getTitle(draft)}</Text>
                      {draft.price != null && (
                        <Text preset="bodySmall" fontWeight="700" mt={2}>{draft.price.toFixed(2)} €</Text>
                      )}
                    </YStack>
                  </Card>
                )
              })}
            </XStack>
          )}
        </YStack>

        {/* Selling menu */}
        <YStack px="$lg" mb="$lg">
          <Text preset="label" mb="$sm">{t('selling')}</Text>
          <Card variant="elevated" br="$xl" overflow="hidden">
            <ListItem
              icon={<YStack w={40} h={40} br="$lg" bg="rgba(116,140,112,0.1)" jc="center" ai="center"><Package size={20} color="#748C70" /></YStack>}
              title={t('drafts')}
              subtitle={draftCount > 0 ? `${draftCount} ${t('products_awaiting_link')}` : t('no_drafts')}
              right={
                <XStack ai="center" gap="$sm">
                  {draftCount > 0 && <Badge variant="primary"><BadgeText>{draftCount}</BadgeText></Badge>}
                  <ChevronRight size={16} color="#858B80" />
                </XStack>
              }
              onPress={() => navigation.navigate('DraftProducts' as any)}
            />
            <ListItem
              icon={<YStack w={40} h={40} br="$lg" bg="rgba(116,140,112,0.1)" jc="center" ai="center"><ShoppingBag size={20} color="#748C70" /></YStack>}
              title={t('flea_markets')}
              subtitle={activeReservations > 0 ? `${activeReservations} ${t('active_reservations_count')}` : t('reserve_selling_spot')}
              right={
                <XStack ai="center" gap="$sm">
                  {activeReservations > 0 && <Badge variant="primary"><BadgeText>{activeReservations}</BadgeText></Badge>}
                  <ChevronRight size={16} color="#858B80" />
                </XStack>
              }
              onPress={() => navigation.navigate('JoinMarket')}
            />
            {hasStores && (
              <ListItem
                icon={<YStack w={40} h={40} br="$lg" bg="rgba(116,140,112,0.1)" jc="center" ai="center"><Store size={20} color="#748C70" /></YStack>}
                title={t('merchant_portal')}
                subtitle={t('manage_your_store')}
                right={<ChevronRight size={16} color="#858B80" />}
                onPress={() => navigation.navigate('MerchantStoreSelect')}
              />
            )}
          </Card>
        </YStack>

        <YStack h={32} />
    </ScreenLayout>
  )
}
